import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Searchable combobox tailored for the passport-number list, which can hold
 * thousands of reserved codes. A native `<select>` with that many `<option>`
 * children is unusable on mobile (laggy popover, tiny text, no search).
 *
 * Public API kept tiny so existing forms can swap it in:
 *   <PassportSelect value={form.passport_number} options={availablePassports}
 *                   onChange={(code) => setForm({...form, passport_number: code})} />
 *
 * `options` items must have `id` and `full_code` fields.
 */
export default function PassportSelect({
  value,
  onChange,
  options = [],
  placeholder = "— Neatribuit —",
  disabled = false,
  testId = "input-passport-number",
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Auto-focus search input when the popover opens.
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Apply a soft cap so the popover stays snappy even with 3000+ options.
  // Users narrow the list via the search box.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 200);
    const out = [];
    for (const opt of options) {
      if ((opt.full_code || "").toLowerCase().includes(q)) {
        out.push(opt);
        if (out.length >= 200) break;
      }
    }
    return out;
  }, [options, query]);

  const totalHidden = options.length - filtered.length;

  const select = (code) => {
    onChange(code);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative" }} data-testid={`${testId}-wrapper`}>
      <button
        type="button"
        className="input-lodge"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        data-testid={testId}
        data-value={value || ""}
        style={{
          width: "100%",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          cursor: disabled ? "not-allowed" : "pointer",
          minHeight: 44,
          fontFamily: value ? "Cinzel, serif" : undefined,
          letterSpacing: value ? "0.08em" : undefined,
        }}
      >
        <span style={{ color: value ? "#0d1b2a" : "#9a9893" }}>
          {value || placeholder}
        </span>
        <ChevronDown size={14} color="#9a7530" />
      </button>

      {open && (
        <div
          data-testid={`${testId}-popover`}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 4,
            background: "#fff",
            border: "1px solid #c9a84c",
            borderRadius: 4,
            boxShadow: "0 10px 30px -8px rgba(13,27,42,0.25)",
            zIndex: 50,
            maxHeight: 320,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "0.5rem 0.75rem",
              borderBottom: "1px solid #ead8a8",
              background: "#fdf4e0",
            }}
          >
            <Search size={14} color="#9a7530" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="RO 0100..."
              data-testid={`${testId}-search`}
              autoComplete="off"
              style={{
                border: 0,
                outline: 0,
                flex: 1,
                fontFamily: "Crimson Text, serif",
                fontSize: "0.95rem",
                background: "transparent",
                color: "#0d1b2a",
              }}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                title="Clear"
                style={{ background: "transparent", border: 0, cursor: "pointer" }}
                data-testid={`${testId}-clear`}
              >
                <X size={14} color="#9a7530" />
              </button>
            )}
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            <button
              type="button"
              onClick={() => select("")}
              data-testid={`${testId}-option-empty`}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "0.55rem 0.85rem",
                background: !value ? "#fdf4e0" : "transparent",
                border: 0,
                color: "#9a7530",
                fontStyle: "italic",
                cursor: "pointer",
                borderBottom: "1px solid #f6efde",
                fontSize: "0.9rem",
              }}
            >
              {placeholder}
            </button>
            {value && !filtered.find((p) => p.full_code === value) && (
              <button
                type="button"
                onClick={() => select(value)}
                data-testid={`${testId}-option-current`}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "0.55rem 0.85rem",
                  background: "#fdf4e0",
                  border: 0,
                  color: "#0d1b2a",
                  cursor: "pointer",
                  borderBottom: "1px solid #f6efde",
                  fontFamily: "Cinzel, serif",
                  letterSpacing: "0.08em",
                }}
              >
                {value} <span style={{ color: "#9a7530", fontStyle: "italic", fontFamily: "inherit" }}>(curent)</span>
              </button>
            )}
            {filtered.length === 0 ? (
              <div
                style={{
                  padding: "1rem",
                  textAlign: "center",
                  color: "#9a7530",
                  fontStyle: "italic",
                  fontSize: "0.85rem",
                }}
                data-testid={`${testId}-empty`}
              >
                {query ? "Niciun rezultat / No match" : "—"}
              </div>
            ) : (
              filtered.map((p) => {
                const selected = p.full_code === value;
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => select(p.full_code)}
                    data-testid={`${testId}-option-${p.full_code.replace(/\s+/g, "")}`}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "0.55rem 0.85rem",
                      background: selected ? "#fdf4e0" : "transparent",
                      border: 0,
                      color: "#0d1b2a",
                      cursor: "pointer",
                      borderBottom: "1px solid #f6efde",
                      fontFamily: "Cinzel, serif",
                      letterSpacing: "0.08em",
                      fontSize: "0.92rem",
                    }}
                  >
                    {p.full_code}
                  </button>
                );
              })
            )}
          </div>
          {totalHidden > 0 && (
            <div
              style={{
                padding: "0.4rem 0.85rem",
                background: "#f6efde",
                fontSize: "0.7rem",
                color: "#9a7530",
                textAlign: "center",
                borderTop: "1px solid #ead8a8",
              }}
              data-testid={`${testId}-overflow-note`}
            >
              {query
                ? t("picker.showingFiltered", { count: filtered.length })
                : t("picker.showingOf", { count: filtered.length, total: options.length })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
